import { assert, deprecate, warn } from '@ember/debug';
import EmberError from '@ember/error';
import EmberObject, { computed, get } from '@ember/object';
import { isNone } from '@ember/utils';
import { DEBUG } from '@glimmer/env';
import Ember from 'ember';
import { coerceId, DeprecatedEvented, errorsArrayToHash, InternalModel, PromiseObject, recordDataFor, recordIdentifierFor, RootState } from '@ember-data/store/-private';
import Errors from './errors';
import { relatedTypesDescriptor, relationshipsByNameDescriptor, relationshipsDescriptor, relationshipsObjectDescriptor } from './system/relationships/ext';
var {
  changeProperties
} = Ember;
function isInvalidError(error) {
  return error && error.isAdapterError === true && error.code === 'InvalidError';
}
function findPossibleInverses(type, inverseType, name, relationshipsSoFar) {
  var possibleRelationships = relationshipsSoFar || [];
  var relationshipMap = get(inverseType, 'relationships');
  if (!relationshipMap) {
    return possibleRelationships;
  }
  var relationshipsForType = relationshipMap.get(type.modelName);
  var relationships = Array.isArray(relationshipsForType) ? relationshipsForType.filter(relationship => {
    var optionsForRelationship = inverseType.metaForProperty(relationship.name).options;
    if (!optionsForRelationship.inverse && optionsForRelationship.inverse !== null) {
      return true;
    }
    return name === optionsForRelationship.inverse;
  }) : null;
  if (relationships) {
    possibleRelationships.push.apply(possibleRelationships, relationships);
  }

  //Recurse to support polymorphism
  if (type.superclass) {
    findPossibleInverses(type.superclass, inverseType, name, possibleRelationships);
  }
  return possibleRelationships;
}
var retrieveFromCurrentState = computed('currentState', function (key) {
  return get(this._internalModel.currentState, key);
}).readOnly();
var isValidRecordData = computed('errors.length', function (key) {
  return !(this.get('errors.length') > 0);
}).readOnly();
var isValid = false /* RECORD_DATA_ERRORS */ ? isValidRecordData : retrieveFromCurrentState;
var isDeletedCP;
if (false /* RECORD_DATA_STATE */) {
  isDeletedCP = computed('currentState', function () {
    var rd = recordDataFor(this);
    if (rd.isDeleted) {
      return rd.isDeleted();
    } else {
      return get(this._internalModel.currentState, 'isDeleted');
    }
  }).readOnly();
} else {
  isDeletedCP = retrieveFromCurrentState;
}
var isNewCP;
if (false /* RECORD_DATA_STATE */) {
  isNewCP = computed('currentState', function () {
    var rd = recordDataFor(this);
    if (rd.isNew) {
      return rd.isNew();
    } else {
      return get(this._internalModel.currentState, 'isNew');
    }
  }).readOnly();
} else {
  isNewCP = retrieveFromCurrentState;
}
var adapterError;
if (false /* REQUEST_SERVICE */) {
  adapterError = computed(function () {
    var request = this._lastError;
    if (!request) {
      return null;
    }
    return request.state === 'rejected' && request.response.data;
  });
} else {
  adapterError = null;
}
var isError;
if (false /* REQUEST_SERVICE */) {
  isError = computed(function () {
    var errorReq = this._errorRequests[this._errorRequests.length - 1];
    if (!errorReq) {
      return false;
    } else {
      return true;
    }
  });
} else {
  isError = false;
}
var isReloading;
if (false /* REQUEST_SERVICE */) {
  isReloading = computed({
    get() {
      if (this._isReloading === undefined) {
        var requests = this.store.getRequestStateService().getPendingRequestsForRecord(recordIdentifierFor(this));
        var value = !!requests.find(req => req.request.data[0].options.isReloading);
        return this._isReloading = value;
      }
      return this._isReloading;
    },
    set(_, value) {
      return this._isReloading = value;
    }
  });
} else {
  isReloading = false;
}

/**
  @class Model
  @module @ember-data/model
  @extends EmberObject
  @uses EmberData.DeprecatedEvented
*/
var Model = EmberObject.extend(DeprecatedEvented, {
  init() {
    this._super(...arguments);
    if (DEBUG) {
      if (!this._internalModel) {
        throw new EmberError('You should not call `create` on a model. Instead, call `store.createRecord` with the attributes you would like to set.');
      }
    }
    if (false /* RECORD_DATA_ERRORS */) {
      this._invalidRequests = [];
    }
    if (false /* REQUEST_SERVICE */) {
      this.store.getRequestStateService().subscribeForRecord(this._internalModel.identifier, request => {
        if (request.state === 'rejected') {
          // TODO filter out queries
          this._lastError = request;
          if (!(request.response && isInvalidError(request.response.data))) {
            this._errorRequests.push(request);
          } else {
            this._invalidRequests.push(request);
          }
        } else if (request.state === 'fulfilled') {
          this._invalidRequests = [];
          this._errorRequests = [];
          this._lastError = null;
        }
        this._notifyNetworkChanges();
      });
      this._errorRequests = [];
      this._lastError = null;
    }
  },
  _notifyNetworkChanges: function () {
    if (false /* REQUEST_SERVICE */) {
      ['isSaving', 'isValid', 'isError', 'adapterError', 'isReloading'].forEach(key => this.notifyPropertyChange(key));
    } else {
      ['isValid'].forEach(key => this.notifyPropertyChange(key));
    }
  },
  /**
    If this property is `true` the record is in the `empty`
    state. Empty is the first state all records enter after they have
    been created. Most records created by the store will quickly
    transition to the `loading` state if data needs to be fetched from
    the server or the `created` state if the record is created on the
    client. A record can also enter the empty state if the adapter is
    unable to locate the record.
     @property isEmpty
    @type {Boolean}
    @readOnly
  */
  isEmpty: retrieveFromCurrentState,
  /**
    If this property is `true` the record is in the `loading` state. A
    record enters this state when the store asks the adapter for its
    data. It remains in this state until the adapter provides the
    requested data.
     @property isLoading
    @type {Boolean}
    @readOnly
  */
  isLoading: retrieveFromCurrentState,
  /**
    If this property is `true` the record is in the `loaded` state. A
    record enters this state when its data is populated. Most of a
    record's lifecycle is spent inside substates of the `loaded`
    state.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('isLoaded'); // true
     store.findRecord('model', 1).then(function(model) {
      model.get('isLoaded'); // true
    });
    ```
     @property isLoaded
    @type {Boolean}
    @readOnly
  */
  isLoaded: retrieveFromCurrentState,
  /**
    If this property is `true` the record is in the `dirty` state. The
    record has local changes that have not yet been saved by the
    adapter. This includes records that have been created (but not yet
    saved) or deleted.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('hasDirtyAttributes'); // true
     store.findRecord('model', 1).then(function(model) {
      model.get('hasDirtyAttributes'); // false
      model.set('foo', 'some value');
      model.get('hasDirtyAttributes'); // true
    });
    ```
     @since 1.13.0
    @property hasDirtyAttributes
    @type {Boolean}
    @readOnly
  */
  hasDirtyAttributes: computed('currentState.isDirty', function () {
    return this.get('currentState.isDirty');
  }),
  /**
    If this property is `true` the record is in the `saving` state. A
    record enters the saving state when `save` is called, but the
    adapter has not yet acknowledged that the changes have been
    persisted to the backend.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('isSaving'); // false
    let promise = record.save();
    record.get('isSaving'); // true
    promise.then(function() {
      record.get('isSaving'); // false
    });
    ```
     @property isSaving
    @type {Boolean}
    @readOnly
  */
  isSaving: retrieveFromCurrentState,
  /**
    If this property is `true` the record is in the `deleted` state
    and has been marked for deletion. When `isDeleted` is true and
    `hasDirtyAttributes` is true, the record is deleted locally but the deletion
    was not yet persisted. When `isSaving` is true, the change is
    in-flight. When both `hasDirtyAttributes` and `isSaving` are false, the
    change has persisted.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('isDeleted');    // false
    record.deleteRecord();
     // Locally deleted
    record.get('isDeleted');           // true
    record.get('hasDirtyAttributes');  // true
    record.get('isSaving');            // false
     // Persisting the deletion
    let promise = record.save();
    record.get('isDeleted');    // true
    record.get('isSaving');     // true
     // Deletion Persisted
    promise.then(function() {
      record.get('isDeleted');          // true
      record.get('isSaving');           // false
      record.get('hasDirtyAttributes'); // false
    });
    ```
     @property isDeleted
    @type {Boolean}
    @readOnly
  */
  isDeleted: isDeletedCP,
  /**
    If this property is `true` the record is in the `new` state. A
    record will be in the `new` state when it has been created on the
    client and the adapter has not yet report that it was successfully
    saved.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('isNew'); // true
     record.save().then(function(model) {
      model.get('isNew'); // false
    });
    ```
     @property isNew
    @type {Boolean}
    @readOnly
  */
  isNew: isNewCP,
  /**
    If this property is `true` the record is in the `valid` state.
     A record will be in the `valid` state when the adapter did not report any
    server-side validation failures.
     @property isValid
    @type {Boolean}
    @readOnly
  */
  isValid: isValid,
  _markInvalidRequestAsClean() {
    if (false /* RECORD_DATA_ERRORS */) {
      this._invalidRequests = [];
      this._notifyNetworkChanges();
    }
  },
  /**
    If the record is in the dirty state this property will report what
    kind of change has caused it to move into the dirty
    state. Possible values are:
     - `created` The record has been created by the client and not yet saved to the adapter.
    - `updated` The record has been updated by the client and not yet saved to the adapter.
    - `deleted` The record has been deleted by the client and not yet saved to the adapter.
     Example
     ```javascript
    let record = store.createRecord('model');
    record.get('dirtyType'); // 'created'
    ```
     @property dirtyType
    @type {String}
    @readOnly
  */
  dirtyType: retrieveFromCurrentState,
  /**
    If `true` the adapter reported that it was unable to save local
    changes to the backend for any reason other than a server-side
    validation error.
     Example
     ```javascript
    record.get('isError'); // false
    record.set('foo', 'valid value');
    record.save().then(null, function() {
      record.get('isError'); // true
    });
    ```
     @property isError
    @type {Boolean}
    @readOnly
  */
  isError: isError,
  _markErrorRequestAsClean() {
    this._errorRequests = [];
    this._lastError = null;
    this._notifyNetworkChanges();
  },
  /**
    If `true` the store is attempting to reload the record from the adapter.
     Example
     ```javascript
    record.get('isReloading'); // false
    record.reload();
    record.get('isReloading'); // true
    ```
     @property isReloading
    @type {Boolean}
    @readOnly
  */
  isReloading: isReloading,
  /**
    All ember models have an id property. This is an identifier
    managed by an external source. These are always coerced to be
    strings before being used internally. Note when declaring the
    attributes for a model it is an error to declare an id
    attribute.
     ```javascript
    let record = store.createRecord('model');
    record.get('id'); // null
     store.findRecord('model', 1).then(function(model) {
      model.get('id'); // '1'
    });
    ```
     @property id
    @type {String}
  */

  /**
    @property currentState
    @private
    @type {Object}
  */
  currentState: RootState.empty,
  // defined here to avoid triggering setUnknownProperty

  /**
   @property _internalModel
   @private
   @type {Object}
   */
  _internalModel: null,
  // defined here to avoid triggering setUnknownProperty

  /**
   @property recordData
   @private
   @type undefined (reserved)
   */
  // will be defined here to avoid triggering setUnknownProperty

  /**
   @property store
   */
  store: null,
  // defined here to avoid triggering setUnknownProperty

  /**
    When the record is in the `invalid` state this object will contain
    any errors returned by the adapter. When present the errors hash
    contains keys corresponding to the invalid property names
    and values which are arrays of Javascript objects with two keys:
     - `message` A string containing the error message from the backend
    - `attribute` The name of the property associated with this error message
     ```javascript
    record.get('errors.length'); // 0
    record.set('foo', 'invalid value');
    record.save().catch(function() {
      record.get('errors').get('foo');
      // [{message: 'foo should be a number.', attribute: 'foo'}]
    });
    ```
     The `errors` property us useful for displaying error messages to
    the user.
     ```handlebars
    <label>Username: {{input value=username}} </label>
    {{#each model.errors.username as |error|}}
      <div class="error">
        {{error.message}}
      </div>
    {{/each}}
    <label>Email: {{input value=email}} </label>
    {{#each model.errors.email as |error|}}
      <div class="error">
        {{error.message}}
      </div>
    {{/each}}
    ```
      You can also access the special `messages` property on the error
    object to get an array of all the error strings.
     ```handlebars
    {{#each model.errors.messages as |message|}}
      <div class="error">
        {{message}}
      </div>
    {{/each}}
    ```
     @property errors
    @type {Errors}
  */
  errors: computed(function () {
    var errors = Errors.create();
    errors._registerHandlers(() => {
      this.send('becameInvalid');
    }, () => {
      this.send('becameValid');
    });
    if (false /* RECORD_DATA_ERRORS */) {
      var recordData = recordDataFor(this);
      var jsonApiErrors;
      if (recordData.getErrors) {
        jsonApiErrors = recordData.getErrors();
        if (jsonApiErrors) {
          var errorsHash = errorsArrayToHash(jsonApiErrors);
          var errorKeys = Object.keys(errorsHash);
          for (var i = 0; i < errorKeys.length; i++) {
            errors._add(errorKeys[i], errorsHash[errorKeys[i]]);
          }
        }
      }
    }
    return errors;
  }).readOnly(),
  invalidErrorsChanged(jsonApiErrors) {
    if (false /* RECORD_DATA_ERRORS */) {
      this._clearErrorMessages();
      var errors = errorsArrayToHash(jsonApiErrors);
      var errorKeys = Object.keys(errors);
      for (var i = 0; i < errorKeys.length; i++) {
        this._addErrorMessageToAttribute(errorKeys[i], errors[errorKeys[i]]);
      }
    }
  },
  _addErrorMessageToAttribute(attribute, message) {
    this.get('errors')._add(attribute, message);
  },
  _clearErrorMessages() {
    this.get('errors')._clear();
  },
  /**
    This property holds the `AdapterError` object with which
    last adapter operation was rejected.
     @property adapterError
    @type {AdapterError}
  */
  adapterError: adapterError,
  /**
    Create a JSON representation of the record, using the serialization
    strategy of the store's adapter.
    `serialize` takes an optional hash as a parameter, currently
    supported options are:
    - `includeId`: `true` if the record's ID should be included in the
      JSON representation.
     @method serialize
    @param {Object} options
    @return {Object} an object whose values are primitive JSON values only
  */
  serialize(options) {
    return this._internalModel.createSnapshot().serialize(options);
  },
  /**
    Fired when the record is ready to be interacted with,
    that is either loaded from the server or created locally.
     @event ready
  */
  ready: null,
  /**
    Fired when the record is loaded from the server.
     @event didLoad
  */
  didLoad: null,
  /**
    Fired when the record is updated.
     @event didUpdate
  */
  didUpdate: null,
  /**
    Fired when a new record is commited to the server.
     @event didCreate
  */
  didCreate: null,
  /**
    Fired when the record is deleted.
     @event didDelete
  */
  didDelete: null,
  /**
    Fired when the record becomes invalid.
     @event becameInvalid
  */
  becameInvalid: null,
  /**
    Fired when the record enters the error state.
     @event becameError
  */
  becameError: null,
  /**
    Fired when the record is rolled back.
     @event rolledBack
  */
  rolledBack: null,
  //TODO Do we want to deprecate these?
  /**
    @method send
    @private
    @param {String} name
    @param {Object} context
  */
  send(name, context) {
    return this._internalModel.send(name, context);
  },
  /**
    @method transitionTo
    @private
    @param {String} name
  */
  transitionTo(name) {
    return this._internalModel.transitionTo(name);
  },
  /**
    Marks the record as deleted but does not save it. You must call
    `save` afterwards if you want to persist it. You might use this
    method if you want to allow the user to still `rollbackAttributes()`
    after a delete was made.
     Example
     ```app/routes/model/delete.js
    import Route from '@ember/routing/route';
     export default Route.extend({
      actions: {
        softDelete() {
          this.get('controller.model').deleteRecord();
        },
        confirm() {
          this.get('controller.model').save();
        },
        undo() {
          this.get('controller.model').rollbackAttributes();
        }
      }
    });
    ```
     @method deleteRecord
  */
  deleteRecord() {
    this._internalModel.deleteRecord();
  },
  /**
    Same as `deleteRecord`, but saves the record immediately.
     Example
     ```app/routes/model/delete.js
    import Route from '@ember/routing/route';
     export default Route.extend({
      actions: {
        delete() {
          this.get('controller.model').destroyRecord().then(function() {
            controller.transitionToRoute('model.index');
          });
        }
      }
    });
    ```
     If you pass an object on the `adapterOptions` property of the options
    argument it will be passed to your adapter via the snapshot
     ```js
    record.destroyRecord({ adapterOptions: { subscribe: false } });
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default MyCustomAdapter.extend({
      deleteRecord(store, type, snapshot) {
        if (snapshot.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    });
    ```
     @method destroyRecord
    @param {Object} options
    @return {Promise} a promise that will be resolved when the adapter returns
    successfully or rejected if the adapter returns with an error.
  */
  destroyRecord(options) {
    this.deleteRecord();
    return this.save(options);
  },
  /**
    Unloads the record from the store. This will not send a delete request
    to your server, it just unloads the record from memory.
     @method unloadRecord
  */
  unloadRecord() {
    if (this.isDestroyed) {
      return;
    }
    this._internalModel.unloadRecord();
  },
  /**
    @method _notifyProperties
    @private
  */
  _notifyProperties(keys) {
    // changeProperties defers notifications until after the delegate
    // and protects with a try...finally block
    // previously used begin...endPropertyChanges but this is private API
    changeProperties(() => {
      var key;
      for (var i = 0, length = keys.length; i < length; i++) {
        key = keys[i];
        this.notifyPropertyChange(key);
      }
    });
  },
  /**
    Returns an object, whose keys are changed properties, and value is
    an [oldProp, newProp] array.
     The array represents the diff of the canonical state with the local state
    of the model. Note: if the model is created locally, the canonical state is
    empty since the adapter hasn't acknowledged the attributes yet:
     Example
     ```app/models/mascot.js
    import Model, { attr } from '@ember-data/model';
     export default Model.extend({
      name: attr('string'),
      isAdmin: attr('boolean', {
        defaultValue: false
      })
    });
    ```
     ```javascript
    let mascot = store.createRecord('mascot');
     mascot.changedAttributes(); // {}
     mascot.set('name', 'Tomster');
    mascot.changedAttributes(); // { name: [undefined, 'Tomster'] }
     mascot.set('isAdmin', true);
    mascot.changedAttributes(); // { isAdmin: [undefined, true], name: [undefined, 'Tomster'] }
     mascot.save().then(function() {
      mascot.changedAttributes(); // {}
       mascot.set('isAdmin', false);
      mascot.changedAttributes(); // { isAdmin: [true, false] }
    });
    ```
     @method changedAttributes
    @return {Object} an object, whose keys are changed properties,
      and value is an [oldProp, newProp] array.
  */
  changedAttributes() {
    return this._internalModel.changedAttributes();
  },
  /**
    If the model `hasDirtyAttributes` this function will discard any unsaved
    changes. If the model `isNew` it will be removed from the store.
     Example
     ```javascript
    record.get('name'); // 'Untitled Document'
    record.set('name', 'Doc 1');
    record.get('name'); // 'Doc 1'
    record.rollbackAttributes();
    record.get('name'); // 'Untitled Document'
    ```
     @since 1.13.0
    @method rollbackAttributes
  */
  rollbackAttributes() {
    this._internalModel.rollbackAttributes();
    if (false /* RECORD_DATA_ERRORS */) {
      this._markInvalidRequestAsClean();
    }
    if (false /* REQUEST_SERVICE */) {
      this._markErrorRequestAsClean();
    }
  },
  /*
    @method _createSnapshot
    @private
  */
  _createSnapshot() {
    return this._internalModel.createSnapshot();
  },
  toStringExtension() {
    // the _internalModel guard exists, because some dev-only deprecation code
    // (addListener via validatePropertyInjections) invokes toString before the
    // object is real.
    return this._internalModel && this._internalModel.id;
  },
  /**
    Save the record and persist any changes to the record to an
    external source via the adapter.
     Example
     ```javascript
    record.set('name', 'Tomster');
    record.save().then(function() {
      // Success callback
    }, function() {
      // Error callback
    });
    ```
    If you pass an object using the `adapterOptions` property of the options
   argument it will be passed to your adapter via the snapshot.
     ```js
    record.save({ adapterOptions: { subscribe: false } });
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default MyCustomAdapter.extend({
      updateRecord(store, type, snapshot) {
        if (snapshot.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    });
    ```
     @method save
    @param {Object} options
    @return {Promise} a promise that will be resolved when the adapter returns
    successfully or rejected if the adapter returns with an error.
  */
  save(options) {
    return PromiseObject.create({
      promise: this._internalModel.save(options).then(() => this)
    });
  },
  /**
    Reload the record from the adapter.
     This will only work if the record has already finished loading.
     Example
     ```app/routes/model/view.js
    import Route from '@ember/routing/route';
     export default Route.extend({
      actions: {
        reload() {
          this.controller.get('model').reload().then(function(model) {
            // do something with the reloaded model
          });
        }
      }
    });
    ```
     @method reload
    @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter request
    @return {Promise} a promise that will be resolved with the record when the
    adapter returns successfully or rejected if the adapter returns
    with an error.
  */
  reload(options) {
    var wrappedAdapterOptions;
    if (typeof options === 'object' && options !== null && options.adapterOptions) {
      wrappedAdapterOptions = {
        adapterOptions: options.adapterOptions
      };
    }
    return PromiseObject.create({
      promise: this._internalModel.reload(wrappedAdapterOptions).then(() => this)
    });
  },
  attr() {
    assert('The `attr` method is not available on Model, a Snapshot was probably expected. Are you passing a Model instead of a Snapshot to your serializer?', false);
  },
  /**
    Get the reference for the specified belongsTo relationship.
     Example
     ```app/models/blog.js
    import Model, { belongsTo } from '@ember-data/model';
     export default Model.extend({
      user: belongsTo({ async: true })
    });
    ```
     ```javascript
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
    let userRef = blog.belongsTo('user');
     // check if the user relationship is loaded
    let isLoaded = userRef.value() !== null;
     // get the record of the reference (null if not yet available)
    let user = userRef.value();
     // get the identifier of the reference
    if (userRef.remoteType() === "id") {
      let id = userRef.id();
    } else if (userRef.remoteType() === "link") {
      let link = userRef.link();
    }
     // load user (via store.findRecord or store.findBelongsTo)
    userRef.load().then(...)
     // or trigger a reload
    userRef.reload().then(...)
     // provide data for reference
    userRef.push({
      type: 'user',
      id: 1,
      attributes: {
        username: "@user"
      }
    }).then(function(user) {
      userRef.value() === user;
    });
    ```
     @method belongsTo
    @param {String} name of the relationship
    @since 2.5.0
    @return {BelongsToReference} reference for this relationship
  */
  belongsTo(name) {
    return this._internalModel.referenceFor('belongsTo', name);
  },
  /**
    Get the reference for the specified hasMany relationship.
     Example
     ```app/models/blog.js
    import Model, { hasMany } from '@ember-data/model';
     export default Model.extend({
      comments: hasMany({ async: true })
    });
     let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          comments: {
            data: [
              { type: 'comment', id: 1 },
              { type: 'comment', id: 2 }
            ]
          }
        }
      }
    });
    let commentsRef = blog.hasMany('comments');
     // check if the comments are loaded already
    let isLoaded = commentsRef.value() !== null;
     // get the records of the reference (null if not yet available)
    let comments = commentsRef.value();
     // get the identifier of the reference
    if (commentsRef.remoteType() === "ids") {
      let ids = commentsRef.ids();
    } else if (commentsRef.remoteType() === "link") {
      let link = commentsRef.link();
    }
     // load comments (via store.findMany or store.findHasMany)
    commentsRef.load().then(...)
     // or trigger a reload
    commentsRef.reload().then(...)
     // provide data for reference
    commentsRef.push([{ type: 'comment', id: 1 }, { type: 'comment', id: 2 }]).then(function(comments) {
      commentsRef.value() === comments;
    });
    ```
     @method hasMany
    @param {String} name of the relationship
    @since 2.5.0
    @return {HasManyReference} reference for this relationship
  */
  hasMany(name) {
    return this._internalModel.referenceFor('hasMany', name);
  },
  /**
   Provides info about the model for debugging purposes
   by grouping the properties into more semantic groups.
    Meant to be used by debugging tools such as the Chrome Ember Extension.
    - Groups all attributes in "Attributes" group.
   - Groups all belongsTo relationships in "Belongs To" group.
   - Groups all hasMany relationships in "Has Many" group.
   - Groups all flags in "Flags" group.
   - Flags relationship CPs as expensive properties.
    @method _debugInfo
   @for Model
   @private
   */
  _debugInfo() {
    var attributes = ['id'];
    var relationships = {};
    var expensiveProperties = [];
    this.eachAttribute((name, meta) => attributes.push(name));
    var groups = [{
      name: 'Attributes',
      properties: attributes,
      expand: true
    }];
    this.eachRelationship((name, relationship) => {
      var properties = relationships[relationship.kind];
      if (properties === undefined) {
        properties = relationships[relationship.kind] = [];
        groups.push({
          name: relationship.kind,
          properties,
          expand: true
        });
      }
      properties.push(name);
      expensiveProperties.push(name);
    });
    groups.push({
      name: 'Flags',
      properties: ['isLoaded', 'hasDirtyAttributes', 'isSaving', 'isDeleted', 'isError', 'isNew', 'isValid']
    });
    return {
      propertyInfo: {
        // include all other mixins / properties (not just the grouped ones)
        includeOtherProperties: true,
        groups: groups,
        // don't pre-calculate unless cached
        expensiveProperties: expensiveProperties
      }
    };
  },
  notifyBelongsToChange(key) {
    this.notifyPropertyChange(key);
  },
  /**
   Given a callback, iterates over each of the relationships in the model,
   invoking the callback with the name of each relationship and its relationship
   descriptor.
     The callback method you provide should have the following signature (all
   parameters are optional):
    ```javascript
   function(name, descriptor);
   ```
    - `name` the name of the current property in the iteration
   - `descriptor` the meta object that describes this relationship
    The relationship descriptor argument is an object with the following properties.
    - **key** <span class="type">String</span> the name of this relationship on the Model
   - **kind** <span class="type">String</span> "hasMany" or "belongsTo"
   - **options** <span class="type">Object</span> the original options hash passed when the relationship was declared
   - **parentType** <span class="type">Model</span> the type of the Model that owns this relationship
   - **type** <span class="type">String</span> the type name of the related Model
    Note that in addition to a callback, you can also pass an optional target
   object that will be set as `this` on the context.
    Example
    ```app/serializers/application.js
   import JSONSerializer from '@ember-data/serializer/json';
    export default JSONSerializer.extend({
    serialize: function(record, options) {
      let json = {};
       record.eachRelationship(function(name, descriptor) {
        if (descriptor.kind === 'hasMany') {
          let serializedHasManyName = name.toUpperCase() + '_IDS';
          json[serializedHasManyName] = record.get(name).mapBy('id');
        }
      });
       return json;
    }
  });
   ```
    @method eachRelationship
   @param {Function} callback the callback to invoke
   @param {any} binding the value to which the callback's `this` should be bound
   */
  eachRelationship(callback, binding) {
    this.constructor.eachRelationship(callback, binding);
  },
  relationshipFor(name) {
    return get(this.constructor, 'relationshipsByName').get(name);
  },
  inverseFor(key) {
    return this.constructor.inverseFor(key, this._internalModel.store);
  },
  notifyHasManyAdded(key) {
    //We need to notifyPropertyChange in the adding case because we need to make sure
    //we fetch the newly added record in case it is unloaded
    //TODO(Igor): Consider whether we could do this only if the record state is unloaded
    this.notifyPropertyChange(key);
  },
  eachAttribute(callback, binding) {
    this.constructor.eachAttribute(callback, binding);
  }
});
if (true /* DEPRECATE_EVENTED_API_USAGE */) {
  /**
  Override the default event firing from Ember.Evented to
  also call methods with the given name.
   @method trigger
  @private
  @param {String} name
  */
  Model.reopen({
    trigger(name) {
      if (true /* DEPRECATE_RECORD_LIFECYCLE_EVENT_METHODS */) {
        var fn = this[name];
        if (typeof fn === 'function') {
          var length = arguments.length;
          var args = new Array(length - 1);
          for (var i = 1; i < length; i++) {
            args[i - 1] = arguments[i];
          }
          fn.apply(this, args);
        }
      }
      var _hasEvent = DEBUG ? this._has(name) : this.has(name);
      if (_hasEvent) {
        this._super(...arguments);
      }
    }
  });
}
if (true /* DEPRECATE_MODEL_DATA */) {
  /**
  @property data
  @private
  @deprecated
  @type {Object}
  */
  Object.defineProperty(Model.prototype, 'data', {
    configurable: false,
    get() {
      deprecate(`Model.data was private and it's use has been deprecated. For public access, use the RecordData API or iterate attributes`, false, {
        id: 'ember-data:Model.data',
        until: '3.9',
        for: '@ember-data/model',
        since: {
          available: '3.15',
          enabled: '3.15'
        }
      });
      return recordDataFor(this)._data;
    }
  });
}
if (true /* DEPRECATE_MODEL_TOJSON */) {
  /**
    Use [JSONSerializer](JSONSerializer.html) to
    get the JSON representation of a record.
     `toJSON` takes an optional hash as a parameter, currently
    supported options are:
     - `includeId`: `true` if the record's ID should be included in the
      JSON representation.
     @method toJSON
    @param {Object} options
    @return {Object} A JSON representation of the object.
  */
  Model.reopen({
    toJSON(options) {
      // container is for lazy transform lookups
      deprecate(`Called the built-in \`toJSON\` on the record "${this.constructor.modelName}:${this.id}". The built-in \`toJSON\` method on instances of classes extending \`Model\` is deprecated. For more information see the link below.`, false, {
        id: 'ember-data:model.toJSON',
        until: '4.0',
        url: 'https://deprecations.emberjs.com/ember-data/v3.x#toc_record-toJSON',
        for: '@ember-data/model',
        since: {
          available: '3.15',
          enabled: '3.15'
        }
      });
      var serializer = this._internalModel.store.serializerFor('-default');
      var snapshot = this._internalModel.createSnapshot();
      return serializer.serialize(snapshot, options);
    }
  });
}
var ID_DESCRIPTOR = {
  configurable: false,
  set(id) {
    var normalizedId = coerceId(id);
    if (normalizedId !== null) {
      this._internalModel.setId(normalizedId);
    }
  },
  get() {
    // the _internalModel guard exists, because some dev-only deprecation code
    // (addListener via validatePropertyInjections) invokes toString before the
    // object is real.
    if (DEBUG) {
      if (!this._internalModel) {
        return;
      }
    }
    get(this._internalModel, '_tag');
    return this._internalModel.id;
  }
};
Object.defineProperty(Model.prototype, 'id', ID_DESCRIPTOR);
if (DEBUG) {
  var lookupDescriptor = function lookupDescriptor(obj, keyName) {
    var current = obj;
    do {
      var descriptor = Object.getOwnPropertyDescriptor(current, keyName);
      if (descriptor !== undefined) {
        return descriptor;
      }
      current = Object.getPrototypeOf(current);
    } while (current !== null);
    return null;
  };
  var isBasicDesc = function isBasicDesc(desc) {
    return !desc || !desc.get && !desc.set && desc.enumerable === true && desc.writable === true && desc.configurable === true;
  };
  var isDefaultEmptyDescriptor = function isDefaultEmptyDescriptor(obj, keyName) {
    var instanceDesc = lookupDescriptor(obj, keyName);
    return isBasicDesc(instanceDesc) && lookupDescriptor(obj.constructor, keyName) === null;
  };
  var lookupDeprecations;
  var _deprecatedLifecycleMethods;
  if (true /* DEPRECATE_RECORD_LIFECYCLE_EVENT_METHODS */) {
    var INSTANCE_DEPRECATIONS = new WeakMap();
    _deprecatedLifecycleMethods = ['becameError', 'becameInvalid', 'didCreate', 'didDelete', 'didLoad', 'didUpdate', 'ready', 'rolledBack'];
    lookupDeprecations = function lookupInstanceDeprecations(instance) {
      var deprecations = INSTANCE_DEPRECATIONS.get(instance);
      if (!deprecations) {
        deprecations = new Set();
        INSTANCE_DEPRECATIONS.set(instance, deprecations);
      }
      return deprecations;
    };
  }
  Model.reopen({
    init() {
      this._super(...arguments);
      if (true /* DEPRECATE_EVENTED_API_USAGE */) {
        this._getDeprecatedEventedInfo = () => `${this._internalModel.modelName}#${this.id}`;
      }
      if (!isDefaultEmptyDescriptor(this, '_internalModel') || !(this._internalModel instanceof InternalModel)) {
        throw new Error(`'_internalModel' is a reserved property name on instances of classes extending Model. Please choose a different property name for ${this.constructor.toString()}`);
      }
      if (!isDefaultEmptyDescriptor(this, 'currentState') || this.get('currentState') !== this._internalModel.currentState) {
        throw new Error(`'currentState' is a reserved property name on instances of classes extending Model. Please choose a different property name for ${this.constructor.toString()}`);
      }
      var idDesc = lookupDescriptor(this, 'id');
      if (idDesc.get !== ID_DESCRIPTOR.get) {
        throw new EmberError(`You may not set 'id' as an attribute on your model. Please remove any lines that look like: \`id: attr('<type>')\` from ${this.constructor.toString()}`);
      }
      if (true /* DEPRECATE_RECORD_LIFECYCLE_EVENT_METHODS */) {
        var lifecycleDeprecations = lookupDeprecations(this.constructor);
        _deprecatedLifecycleMethods.forEach(methodName => {
          if (typeof this[methodName] === 'function' && !lifecycleDeprecations.has(methodName)) {
            deprecate(`You defined a \`${methodName}\` method for ${this.constructor.toString()} but lifecycle events for models have been deprecated.`, false, {
              id: 'ember-data:record-lifecycle-event-methods',
              until: '4.0',
              url: 'https://deprecations.emberjs.com/ember-data/v3.x#toc_record-lifecycle-event-methods',
              for: '@ember-data/model',
              since: {
                available: '3.12',
                enabled: '3.12'
              }
            });
            lifecycleDeprecations.add(methodName);
          }
        });
      }
    }
  });
}
Model.reopenClass({
  isModel: true,
  /**
    Create should only ever be called by the store. To create an instance of a
    `Model` in a dirty state use `store.createRecord`.
    To create instances of `Model` in a clean state, use `store.push`
     @method create
    @private
    @static
  */

  /**
   Represents the model's class name as a string. This can be used to look up the model's class name through
   `Store`'s modelFor method.
    `modelName` is generated for you by Ember Data. It will be a lowercased, dasherized string.
   For example:
    ```javascript
   store.modelFor('post').modelName; // 'post'
   store.modelFor('blog-post').modelName; // 'blog-post'
   ```
    The most common place you'll want to access `modelName` is in your serializer's `payloadKeyFromModelName` method. For example, to change payload
   keys to underscore (instead of dasherized), you might use the following code:
    ```javascript
   import RESTSerializer from '@ember-data/serializer/rest';
   import { underscore } from '@ember/string';
    export default const PostSerializer = RESTSerializer.extend({
     payloadKeyFromModelName(modelName) {
       return underscore(modelName);
     }
   });
   ```
   @property modelName
   @type String
   @readonly
   @static
  */
  modelName: null,
  /*
   These class methods below provide relationship
   introspection abilities about relationships.
    A note about the computed properties contained here:
    **These properties are effectively sealed once called for the first time.**
   To avoid repeatedly doing expensive iteration over a model's fields, these
   values are computed once and then cached for the remainder of the runtime of
   your application.
    If your application needs to modify a class after its initial definition
   (for example, using `reopen()` to add additional attributes), make sure you
   do it before using your model with the store, which uses these properties
   extensively.
   */

  /**
   For a given relationship name, returns the model type of the relationship.
    For example, if you define a model like this:
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default Model.extend({
      comments: hasMany('comment')
    });
   ```
    Calling `store.modelFor('post').typeForRelationship('comments', store)` will return `Comment`.
    @method typeForRelationship
   @static
   @param {String} name the name of the relationship
   @param {store} store an instance of Store
   @return {Model} the type of the relationship, or undefined
   */
  typeForRelationship(name, store) {
    var relationship = get(this, 'relationshipsByName').get(name);
    return relationship && store.modelFor(relationship.type);
  },
  inverseMap: computed(function () {
    return Object.create(null);
  }),
  /**
   Find the relationship which is the inverse of the one asked for.
    For example, if you define models like this:
    ```app/models/post.js
   import Model, { hasMany } from '@ember-data/model';
    export default Model.extend({
      comments: hasMany('message')
    });
   ```
    ```app/models/message.js
   import Model, { belongsTo } from '@ember-data/model';
    export default Model.extend({
      owner: belongsTo('post')
    });
   ```
    ``` js
   store.modelFor('post').inverseFor('comments', store) // { type: App.Message, name: 'owner', kind: 'belongsTo' }
   store.modelFor('message').inverseFor('owner', store) // { type: App.Post, name: 'comments', kind: 'hasMany' }
   ```
    @method inverseFor
   @static
   @param {String} name the name of the relationship
   @param {Store} store
   @return {Object} the inverse relationship, or null
   */
  inverseFor(name, store) {
    var inverseMap = get(this, 'inverseMap');
    if (inverseMap[name]) {
      return inverseMap[name];
    } else {
      var inverse = this._findInverseFor(name, store);
      inverseMap[name] = inverse;
      return inverse;
    }
  },
  //Calculate the inverse, ignoring the cache
  _findInverseFor(name, store) {
    var inverseType = this.typeForRelationship(name, store);
    if (!inverseType) {
      return null;
    }
    var propertyMeta = this.metaForProperty(name);
    //If inverse is manually specified to be null, like  `comments: hasMany('message', { inverse: null })`
    var options = propertyMeta.options;
    if (options.inverse === null) {
      return null;
    }
    var inverseName, inverseKind, inverse, inverseOptions;

    //If inverse is specified manually, return the inverse
    if (options.inverse) {
      inverseName = options.inverse;
      inverse = get(inverseType, 'relationshipsByName').get(inverseName);
      assert("We found no inverse relationships by the name of '" + inverseName + "' on the '" + inverseType.modelName + "' model. This is most likely due to a missing attribute on your model definition.", !isNone(inverse));

      // TODO probably just return the whole inverse here
      inverseKind = inverse.kind;
      inverseOptions = inverse.options;
    } else {
      //No inverse was specified manually, we need to use a heuristic to guess one
      if (propertyMeta.type === propertyMeta.parentModelName) {
        warn(`Detected a reflexive relationship by the name of '${name}' without an inverse option. Look at https://guides.emberjs.com/current/models/relationships/#toc_reflexive-relations for how to explicitly specify inverses.`, false, {
          id: 'ds.model.reflexive-relationship-without-inverse'
        });
      }
      var possibleRelationships = findPossibleInverses(this, inverseType, name);
      if (possibleRelationships.length === 0) {
        return null;
      }
      var filteredRelationships = possibleRelationships.filter(possibleRelationship => {
        var optionsForRelationship = inverseType.metaForProperty(possibleRelationship.name).options;
        return name === optionsForRelationship.inverse;
      });
      assert("You defined the '" + name + "' relationship on " + this + ', but you defined the inverse relationships of type ' + inverseType.toString() + ' multiple times. Look at https://guides.emberjs.com/current/models/relationships/#toc_explicit-inverses for how to explicitly specify inverses', filteredRelationships.length < 2);
      if (filteredRelationships.length === 1) {
        possibleRelationships = filteredRelationships;
      }
      assert("You defined the '" + name + "' relationship on " + this + ', but multiple possible inverse relationships of type ' + this + ' were found on ' + inverseType + '. Look at https://guides.emberjs.com/current/models/relationships/#toc_explicit-inverses for how to explicitly specify inverses', possibleRelationships.length === 1);
      inverseName = possibleRelationships[0].name;
      inverseKind = possibleRelationships[0].kind;
      inverseOptions = possibleRelationships[0].options;
    }
    assert(`The ${inverseType.modelName}:${inverseName} relationship declares 'inverse: null', but it was resolved as the inverse for ${this.modelName}:${name}.`, !inverseOptions || inverseOptions.inverse !== null);
    return {
      type: inverseType,
      name: inverseName,
      kind: inverseKind,
      options: inverseOptions
    };
  },
  /**
   The model's relationships as a map, keyed on the type of the
   relationship. The value of each entry is an array containing a descriptor
   for each relationship with that type, describing the name of the relationship
   as well as the type.
    For example, given the following model definition:
    ```app/models/blog.js
   import Model, { belongsTo, hasMany } from '@ember-data/model';
    export default Model.extend({
      users: hasMany('user'),
      owner: belongsTo('user'),
      posts: hasMany('post')
    });
   ```
    This computed property would return a map describing these
   relationships, like this:
    ```javascript
   import Ember from 'ember';
   import Blog from 'app/models/blog';
   import User from 'app/models/user';
   import Post from 'app/models/post';
    let relationships = Ember.get(Blog, 'relationships');
   relationships.get('user');
   //=> [ { name: 'users', kind: 'hasMany' },
   //     { name: 'owner', kind: 'belongsTo' } ]
   relationships.get('post');
   //=> [ { name: 'posts', kind: 'hasMany' } ]
   ```
    @property relationships
   @static
   @type Map
   @readOnly
   */

  relationships: relationshipsDescriptor,
  /**
   A hash containing lists of the model's relationships, grouped
   by the relationship kind. For example, given a model with this
   definition:
    ```app/models/blog.js
   import Model, { belongsTo, hasMany } from '@ember-data/model';
    export default Model.extend({
      users: hasMany('user'),
      owner: belongsTo('user'),
       posts: hasMany('post')
    });
   ```
    This property would contain the following:
    ```javascript
   import Ember from 'ember';
   import Blog from 'app/models/blog';
    let relationshipNames = Ember.get(Blog, 'relationshipNames');
   relationshipNames.hasMany;
   //=> ['users', 'posts']
   relationshipNames.belongsTo;
   //=> ['owner']
   ```
    @property relationshipNames
   @static
   @type Object
   @readOnly
   */
  relationshipNames: computed(function () {
    var names = {
      hasMany: [],
      belongsTo: []
    };
    this.eachComputedProperty((name, meta) => {
      if (meta.isRelationship) {
        names[meta.kind].push(name);
      }
    });
    return names;
  }),
  /**
   An array of types directly related to a model. Each type will be
   included once, regardless of the number of relationships it has with
   the model.
    For example, given a model with this definition:
    ```app/models/blog.js
   import Model, { belongsTo, hasMany } from '@ember-data/model';
    export default Model.extend({
      users: hasMany('user'),
      owner: belongsTo('user'),
       posts: hasMany('post')
    });
   ```
    This property would contain the following:
    ```javascript
   import Ember from 'ember';
   import Blog from 'app/models/blog';
    let relatedTypes = Ember.get(Blog, 'relatedTypes');
   //=> [ User, Post ]
   ```
    @property relatedTypes
   @static
   @type Ember.Array
   @readOnly
   */
  relatedTypes: relatedTypesDescriptor,
  /**
   A map whose keys are the relationships of a model and whose values are
   relationship descriptors.
    For example, given a model with this
   definition:
    ```app/models/blog.js
   import Model, { belongsTo, hasMany } from '@ember-data/model';
    export default Model.extend({
      users: hasMany('user'),
      owner: belongsTo('user'),
       posts: hasMany('post')
    });
   ```
    This property would contain the following:
    ```javascript
   import Ember from 'ember';
   import Blog from 'app/models/blog';
    let relationshipsByName = Ember.get(Blog, 'relationshipsByName');
   relationshipsByName.get('users');
   //=> { key: 'users', kind: 'hasMany', type: 'user', options: Object, isRelationship: true }
   relationshipsByName.get('owner');
   //=> { key: 'owner', kind: 'belongsTo', type: 'user', options: Object, isRelationship: true }
   ```
    @property relationshipsByName
   @static
   @type Map
   @readOnly
   */
  relationshipsByName: relationshipsByNameDescriptor,
  relationshipsObject: relationshipsObjectDescriptor,
  /**
   A map whose keys are the fields of the model and whose values are strings
   describing the kind of the field. A model's fields are the union of all of its
   attributes and relationships.
    For example:
    ```app/models/blog.js
   import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
    export default Model.extend({
      users: hasMany('user'),
      owner: belongsTo('user'),
       posts: hasMany('post'),
       title: attr('string')
    });
   ```
    ```js
   import Ember from 'ember';
   import Blog from 'app/models/blog';
    let fields = Ember.get(Blog, 'fields');
   fields.forEach(function(kind, field) {
      console.log(field, kind);
    });
    // prints:
   // users, hasMany
   // owner, belongsTo
   // posts, hasMany
   // title, attribute
   ```
    @property fields
   @static
   @type Map
   @readOnly
   */
  fields: computed(function () {
    var map = new Map();
    this.eachComputedProperty((name, meta) => {
      if (meta.isRelationship) {
        map.set(name, meta.kind);
      } else if (meta.isAttribute) {
        map.set(name, 'attribute');
      }
    });
    return map;
  }).readOnly(),
  /**
   Given a callback, iterates over each of the relationships in the model,
   invoking the callback with the name of each relationship and its relationship
   descriptor.
    @method eachRelationship
   @static
   @param {Function} callback the callback to invoke
   @param {any} binding the value to which the callback's `this` should be bound
   */
  eachRelationship(callback, binding) {
    get(this, 'relationshipsByName').forEach((relationship, name) => {
      callback.call(binding, name, relationship);
    });
  },
  /**
   Given a callback, iterates over each of the types related to a model,
   invoking the callback with the related type's class. Each type will be
   returned just once, regardless of how many different relationships it has
   with a model.
    @method eachRelatedType
   @static
   @param {Function} callback the callback to invoke
   @param {any} binding the value to which the callback's `this` should be bound
   */
  eachRelatedType(callback, binding) {
    var relationshipTypes = get(this, 'relatedTypes');
    for (var i = 0; i < relationshipTypes.length; i++) {
      var type = relationshipTypes[i];
      callback.call(binding, type);
    }
  },
  determineRelationshipType(knownSide, store) {
    var knownKey = knownSide.key;
    var knownKind = knownSide.kind;
    var inverse = this.inverseFor(knownKey, store);
    // let key;
    var otherKind;
    if (!inverse) {
      return knownKind === 'belongsTo' ? 'oneToNone' : 'manyToNone';
    }

    // key = inverse.name;
    otherKind = inverse.kind;
    if (otherKind === 'belongsTo') {
      return knownKind === 'belongsTo' ? 'oneToOne' : 'manyToOne';
    } else {
      return knownKind === 'belongsTo' ? 'oneToMany' : 'manyToMany';
    }
  },
  /**
   A map whose keys are the attributes of the model (properties
   described by attr) and whose values are the meta object for the
   property.
    Example
    ```app/models/person.js
   import Model, { attr } from '@ember-data/model';
    export default Model.extend({
      firstName: attr('string'),
      lastName: attr('string'),
      birthday: attr('date')
    });
   ```
    ```javascript
   import Ember from 'ember';
   import Person from 'app/models/person';
    let attributes = Ember.get(Person, 'attributes')
    attributes.forEach(function(meta, name) {
      console.log(name, meta);
    });
    // prints:
   // firstName {type: "string", isAttribute: true, options: Object, parentType: function, name: "firstName"}
   // lastName {type: "string", isAttribute: true, options: Object, parentType: function, name: "lastName"}
   // birthday {type: "date", isAttribute: true, options: Object, parentType: function, name: "birthday"}
   ```
    @property attributes
   @static
   @type {Map}
   @readOnly
   */
  attributes: computed(function () {
    var map = new Map();
    this.eachComputedProperty((name, meta) => {
      if (meta.isAttribute) {
        assert("You may not set `id` as an attribute on your model. Please remove any lines that look like: `id: attr('<type>')` from " + this.toString(), name !== 'id');
        meta.name = name;
        map.set(name, meta);
      }
    });
    return map;
  }).readOnly(),
  /**
   A map whose keys are the attributes of the model (properties
   described by attr) and whose values are type of transformation
   applied to each attribute. This map does not include any
   attributes that do not have an transformation type.
    Example
    ```app/models/person.js
   import Model, { attr } from '@ember-data/model';
    export default Model.extend({
      firstName: attr(),
      lastName: attr('string'),
      birthday: attr('date')
    });
   ```
    ```javascript
   import Ember from 'ember';
   import Person from 'app/models/person';
    let transformedAttributes = Ember.get(Person, 'transformedAttributes')
    transformedAttributes.forEach(function(field, type) {
      console.log(field, type);
    });
    // prints:
   // lastName string
   // birthday date
   ```
    @property transformedAttributes
   @static
   @type {Map}
   @readOnly
   */
  transformedAttributes: computed(function () {
    var map = new Map();
    this.eachAttribute((key, meta) => {
      if (meta.type) {
        map.set(key, meta.type);
      }
    });
    return map;
  }).readOnly(),
  /**
   Iterates through the attributes of the model, calling the passed function on each
   attribute.
    The callback method you provide should have the following signature (all
   parameters are optional):
    ```javascript
   function(name, meta);
   ```
    - `name` the name of the current property in the iteration
   - `meta` the meta object for the attribute property in the iteration
    Note that in addition to a callback, you can also pass an optional target
   object that will be set as `this` on the context.
    Example
    ```javascript
   import Model, { attr } from '@ember-data/model';
    let Person = Model.extend({
      firstName: attr('string'),
      lastName: attr('string'),
      birthday: attr('date')
    });
    Person.eachAttribute(function(name, meta) {
      console.log(name, meta);
    });
    // prints:
   // firstName {type: "string", isAttribute: true, options: Object, parentType: function, name: "firstName"}
   // lastName {type: "string", isAttribute: true, options: Object, parentType: function, name: "lastName"}
   // birthday {type: "date", isAttribute: true, options: Object, parentType: function, name: "birthday"}
   ```
    @method eachAttribute
   @param {Function} callback The callback to execute
   @param {Object} [binding] the value to which the callback's `this` should be bound
   @static
   */
  eachAttribute(callback, binding) {
    get(this, 'attributes').forEach((meta, name) => {
      callback.call(binding, name, meta);
    });
  },
  /**
   Iterates through the transformedAttributes of the model, calling
   the passed function on each attribute. Note the callback will not be
   called for any attributes that do not have an transformation type.
    The callback method you provide should have the following signature (all
   parameters are optional):
    ```javascript
   function(name, type);
   ```
    - `name` the name of the current property in the iteration
   - `type` a string containing the name of the type of transformed
   applied to the attribute
    Note that in addition to a callback, you can also pass an optional target
   object that will be set as `this` on the context.
    Example
    ```javascript
   import Model, { attr } from '@ember-data/model';
    let Person = Model.extend({
      firstName: attr(),
      lastName: attr('string'),
      birthday: attr('date')
    });
    Person.eachTransformedAttribute(function(name, type) {
      console.log(name, type);
    });
    // prints:
   // lastName string
   // birthday date
   ```
    @method eachTransformedAttribute
   @param {Function} callback The callback to execute
   @param {Object} [binding] the value to which the callback's `this` should be bound
   @static
   */
  eachTransformedAttribute(callback, binding) {
    get(this, 'transformedAttributes').forEach((type, name) => {
      callback.call(binding, name, type);
    });
  },
  /**
   Returns the name of the model class.
    @method toString
   @static
   */
  toString() {
    return `model:${get(this, 'modelName')}`;
  }
});
export default Model;